import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calculator,
  Save,
  ArrowLeft,
  Building2,
  FileText,
  IndianRupee,
  Percent,
  TrendingUp,
  TrendingDown,
  Receipt,
  Briefcase,
  Hash,
  Layers,
  Plus,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import departmentRoleService from '../../../api/services/departmentRoleService';
import { createSalaryStructure, updateSalaryStructure, getSalaryStructure } from '../../../api/salaryStructureApi';
import { toast, Toaster } from 'react-hot-toast';
import { salaryComponentService } from '../../../api/crm/config/configService';
import { INVALID_CHARS_REGEX } from "../../../utils/formValidation";

// Helper function to convert value to number
const toNumber = (value) => {
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

// Helper function to format decimal value
const formatDecimalValue = (value, decimalPlaces = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '';
  return parseFloat(Number(value).toFixed(decimalPlaces)).toString();
};

// Helper function to sanitize decimal input
const sanitizeDecimalInput = (value, decimalPlaces = 2) => {
  if (value === null || value === undefined) return '';
  let sanitized = String(value).replace(/[^0-9.]/g, '');
  const firstDot = sanitized.indexOf('.');
  if (firstDot !== -1) {
    const beforeDot = sanitized.slice(0, firstDot + 1);
    const afterDot = sanitized.slice(firstDot + 1).replace(/\./g, '');
    sanitized = beforeDot + afterDot;
  }
  if (sanitized === '.') return '0.';
  const [intPart = '', decPart] = sanitized.split('.');
  let formatted = intPart;
  if (decPart !== undefined) {
    formatted += '.' + decPart.slice(0, decimalPlaces);
  }
  return formatted;
};

const getInitialFormData = () => ({
  name: '',
  code: '',
  department: '',
  grade: '',
  ctc: '',
  status: 'active',
  components: []
});

const getInitialComponentState = () => ({
  name: '',
  type: 'earning',
  value: '',
  percentageValue: ''
});

const CreateSalaryStructure = ({ id, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Form data
  const [formData, setFormData] = useState(() => getInitialFormData());

  // New component data
  const [newComponent, setNewComponent] = useState(() => getInitialComponentState());

  // Validation errors
  const [componentNameError, setComponentNameError] = useState(false);
  const [componentNameAlreadyUsed, setComponentNameAlreadyUsed] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    name: '',
    code: '',
    department: '',
    grade: '',
    ctc: '',
    components: ''
  });

  // Ref to track if amount error toast is currently showing
  const amountErrorToastActiveRef = useRef(false);
  const structureIdFromQuery = searchParams.get('structureId');
  const [editStructurePayload, setEditStructurePayload] = useState(null);
  const [isLoadingStructure, setIsLoadingStructure] = useState(false);
  const [loadStructureError, setLoadStructureError] = useState(null);
  const [structureFetchAttempt, setStructureFetchAttempt] = useState(0);

  // Use id prop if available, otherwise fallback to URL param
  const editingStructureId = id || structureIdFromQuery || null;
  const isEditMode = Boolean(editingStructureId);
  const pageTitle = isEditMode ? 'Update Salary Structure' : 'Create Salary Structure';
  const pageSubtitle = isEditMode
    ? 'Modify this salary structure and adjust its components'
    : 'Define a new salary structure with components and calculations';

  // Fetch active salary components from API
  const { data: salaryComponentsResult = [], isLoading: loadingComponents } = useQuery({
    queryKey: ['salaryComponents', 'active'],
    queryFn: async () => {
      const result = await salaryComponentService.getAll({ isActive: true });
      return result?.data || [];
    }
  });

  useEffect(() => {
    let isMounted = true;

    const fetchStructure = async () => {
      if (!isEditMode) {
        setEditStructurePayload(null);
        setLoadStructureError(null);
        setIsLoadingStructure(false);
        return;
      }

      setIsLoadingStructure(true);
      setLoadStructureError(null);

      try {
        const response = await getSalaryStructure(editingStructureId);
        const structureData = response?.data || response;
        if (isMounted) {
          setEditStructurePayload(structureData);
        }
      } catch (error) {
        if (isMounted) {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load salary structure';
          setLoadStructureError(errorMessage);
          setEditStructurePayload(null);
          toast.error(errorMessage);
        }
      } finally {
        if (isMounted) {
          setIsLoadingStructure(false);
        }
      }
    };

    fetchStructure();

    return () => {
      isMounted = false;
    };
  }, [isEditMode, editingStructureId, structureFetchAttempt]);

  useEffect(() => {
    if (!isEditMode) {
      setFormData(getInitialFormData());
      setComponentNameError(false);
      setComponentNameAlreadyUsed(false);
      setNewComponent(getInitialComponentState());
      return;
    }

    if (!editStructurePayload) return;

    const normalizedComponents = (editStructurePayload.components || []).map((component, index) => {
      const normalizedValue = toNumber(component.value);
      const normalizedPercentage = component.percentageValue === null || component.percentageValue === undefined
        ? 0
        : toNumber(component.percentageValue);

      return {
        ...component,
        id: component._id || component.id || `component-${index}-${component.name || 'item'}`,
        value: normalizedValue,
        percentageValue: normalizedPercentage
      };
    });

    const ctcValue = editStructurePayload.ctc ?? '';

    setFormData({
      name: editStructurePayload.name || '',
      code: editStructurePayload.code || '',
      department: typeof editStructurePayload.department === 'object' ? (editStructurePayload.department.name || editStructurePayload.department.departmentName || '') : (editStructurePayload.department || ''),
      grade: editStructurePayload.grade || '',
      ctc: ctcValue === '' ? '' : String(ctcValue),
      status: editStructurePayload.status || 'active',
      components: normalizedComponents
    });

    setComponentNameError(false);
    setComponentNameAlreadyUsed(false);
    setNewComponent(getInitialComponentState());
  }, [isEditMode, editStructurePayload]);

  // Fetch departments from API
  const {
    data: departmentsData = [],
    isLoading: loadingDepartments,
  } = useQuery({
    queryKey: ['departments', 'active', 'names'],
    queryFn: async () => {
      const result = await departmentRoleService.getDepartments({ isActive: 'all' });

      let departmentsData = [];
      if (Array.isArray(result)) {
        departmentsData = result;
      } else if (result && result.data) {
        departmentsData = Array.isArray(result.data) ? result.data : result.data.departments || [];
      } else if (result && result.responseCode === 200) {
        departmentsData = result.data?.departments || result.data || [];
      }

      // Filter only active departments
      const activeDepartments = departmentsData
        .filter(dept => {
          if (typeof dept === 'string') {
            return true; // If it's a string, assume it's active
          } else if (dept && typeof dept === 'object') {
            return dept.isActive === true || dept.isActive === 'true';
          }
          return false;
        })
        .map(dept => {
          if (typeof dept === 'string') {
            return dept;
          } else if (dept && typeof dept === 'object') {
            return dept.name || dept.departmentName || dept.title || dept.department || '';
          }
          return '';
        })
        .filter(Boolean)
        .sort();

      return activeDepartments;
    },
    onError: (err) => {
    }
  });

  const handleBack = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate(-1);
  };

  const retryLoadStructure = useCallback(() => {
    setStructureFetchAttempt(prev => prev + 1);
  }, []);

  // Handle text input for salary structure fields
  const handleTextInput = useCallback((field, value) => {
    // Standard sanitization using centralised regex
    const sanitized = value.replace(INVALID_CHARS_REGEX, '');

    // Enforce maxLength based on field
    let processedValue = sanitized;
    if (field === 'name' && processedValue.length > 100) {
      processedValue = processedValue.slice(0, 100);
    } else if (field === 'code' && processedValue.length > 20) {
      processedValue = processedValue.slice(0, 20);
    } else if (field === 'grade' && processedValue.length > 20) {
      processedValue = processedValue.slice(0, 20);
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Clear error for this field when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }, [fieldErrors]);

  // Handle field blur validation
  const handleFieldBlur = useCallback((field) => {
    const errors = { ...fieldErrors };

    if (field === 'name' && (!formData.name || !formData.name.trim())) {
      errors.name = 'Structure name is required';
    } else if (field === 'code' && (!formData.code || !formData.code.trim())) {
      errors.code = 'Structure code is required';
    } else if (field === 'department' && (!formData.department || !formData.department.trim())) {
      errors.department = 'Department is required';
    } else if (field === 'grade' && (!formData.grade || !formData.grade.trim())) {
      errors.grade = 'Level is required';
    } else if (field === 'ctc' && (!formData.ctc || parseFloat(formData.ctc) <= 0)) {
      errors.ctc = 'CTC must be greater than 0';
    }

    setFieldErrors(errors);
  }, [formData, fieldErrors]);

  // Handle CTC input with max limit validation
  const handleCTCChange = useCallback((value) => {
    // Remove any non-digit characters
    const sanitized = value.replace(/\D/g, '');

    // Convert to number and check max limit
    const maxLimit = 10000000; // 1 crore
    let processedValue = sanitized;

    if (sanitized && parseInt(sanitized) > maxLimit) {
      processedValue = String(maxLimit);
    }

    // Ensure non-negative
    if (processedValue && parseInt(processedValue) < 0) {
      processedValue = '0';
    }

    setFormData(prev => ({
      ...prev,
      ctc: processedValue
    }));

    // Clear error for CTC when user starts typing
    if (fieldErrors.ctc) {
      setFieldErrors(prev => ({
        ...prev,
        ctc: ''
      }));
    }
  }, [fieldErrors]);

  // Handle CTC key down to prevent invalid characters
  const handleCTCKeyDown = (e) => {
    // Prevent negative sign, decimal point, and 'e' or 'E' (scientific notation)
    if (e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  // Handle key press for text inputs - allow approved symbols
  const handleTextKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  // Calculate percentage from amount based on CTC or reimbursement limit
  const getPercentageFromAmount = useCallback((amount, componentType = 'earning') => {
    if (amount === null || amount === undefined) return null;

    // For reimbursement, calculate based on ₹1,00,000
    if (componentType === 'reimbursement') {
      const reimbursementLimit = 100000;
      const percentage = (amount / reimbursementLimit) * 100;
      // Round to 3 decimal places and cap at 100
      return Math.min(100, Math.round(percentage * 1000) / 1000);
    }

    // For other types, calculate based on CTC
    const ctc = parseFloat(formData.ctc) || 0;
    if (!ctc) return null;
    const percentage = (amount / ctc) * 100;
    // Round to 3 decimal places and cap at 100
    return Math.min(100, Math.round(percentage * 1000) / 1000);
  }, [formData.ctc]);

  // Calculate amount from percentage based on CTC or reimbursement limit
  const getAmountFromPercentage = useCallback((percentage, componentType = 'earning') => {
    if (percentage === null || percentage === undefined) return null;

    // For reimbursement, calculate based on ₹1,00,000
    if (componentType === 'reimbursement') {
      const reimbursementLimit = 100000;
      return Math.round((percentage / 100) * reimbursementLimit);
    }

    // For other types, calculate based on CTC
    const ctc = parseFloat(formData.ctc) || 0;
    if (!ctc) return null;
    return Math.round((percentage / 100) * ctc);
  }, [formData.ctc]);

  // Calculate totals from components
  const calculateTotals = useCallback(() => {
    const earnings = formData.components
      .filter(comp => comp.type === 'earning')
      .reduce((sum, comp) => sum + toNumber(comp.value), 0);

    const deductions = formData.components
      .filter(comp => comp.type === 'deduction')
      .reduce((sum, comp) => sum + toNumber(comp.value), 0);

    const reimbursements = formData.components
      .filter(comp => comp.type === 'reimbursement')
      .reduce((sum, comp) => sum + toNumber(comp.value), 0);

    // Net Pay = Earnings + Reimbursements
    return {
      earnings,
      deductions,
      reimbursements,
      net: earnings + reimbursements
    };
  }, [formData.components]);

  // Calculate maximum allowed amount for a component based on CTC
  const getMaxAllowedAmount = useCallback((componentType, excludeComponentId = null) => {
    if (componentType === 'reimbursement') {
      return 100000; // Reimbursement limit
    }

    const ctc = parseFloat(formData.ctc) || 0;
    if (!ctc) return 10000000; // If no CTC, allow up to max limit

    // Calculate current total of earnings and deductions (excluding reimbursements and the component being edited)
    const currentNonReimbursementTotal = formData.components
      .filter(comp => comp.id !== excludeComponentId && comp.type !== 'reimbursement')
      .reduce((sum, comp) => sum + toNumber(comp.value), 0);

    // Remaining CTC available
    const remainingCTC = Math.max(0, ctc - currentNonReimbursementTotal);

    return remainingCTC;
  }, [formData.ctc, formData.components]);

  // Calculate available amount for display
  const getAvailableAmount = useCallback((componentType) => {
    if (componentType === 'reimbursement') {
      return 100000; // Reimbursement has fixed limit
    }

    const ctc = parseFloat(formData.ctc) || 0;
    if (!ctc) return 0;

    // Calculate current total of earnings and deductions (excluding reimbursements)
    const currentNonReimbursementTotal = formData.components
      .filter(comp => comp.type !== 'reimbursement')
      .reduce((sum, comp) => sum + toNumber(comp.value), 0);

    // Remaining CTC available
    const remainingCTC = Math.max(0, ctc - currentNonReimbursementTotal);

    return remainingCTC;
  }, [formData.ctc, formData.components]);

  // Handle component field changes
  const handleComponentChange = useCallback((field, value) => {
    let processedValue = value;

    // Number fields: only allow digits, ensure positive
    if (['value', 'percentageValue'].includes(field)) {
      // Use 3 decimal places for percentage, 2 for amount
      const decimalPlaces = field === 'percentageValue' ? 3 : 2;
      processedValue = sanitizeDecimalInput(value, decimalPlaces);
      if (processedValue && parseFloat(processedValue) < 0) processedValue = '0';

      if (field === 'value') {
        // Get max allowed based on CTC (earnings + deductions must not exceed CTC)
        const maxAllowed = getMaxAllowedAmount(newComponent.type);
        if (processedValue && parseFloat(processedValue) > maxAllowed) {
          processedValue = String(maxAllowed);
          const limitLabel = newComponent.type === 'reimbursement'
            ? 'reimbursement limit (₹1,00,000)'
            : 'remaining CTC limit';

          // Only show new toast if previous one is closed
          if (!amountErrorToastActiveRef.current) {
            toast.error(`Amount cannot exceed ${limitLabel} (₹${maxAllowed.toLocaleString('en-IN')}).`);
            amountErrorToastActiveRef.current = true;

            // Clear the flag after toast duration (default is 4000ms from Toaster config)
            setTimeout(() => {
              amountErrorToastActiveRef.current = false;
            }, 4000);
          }
        }
      } else if (field === 'percentageValue' && processedValue && parseFloat(processedValue) > 100) {
        processedValue = '100';
      }
    }

    setNewComponent(prev => {
      const updated = {
        ...prev,
        [field]: processedValue || value
      };

      // When value changes, calculate percentageValue
      if (field === 'value') {
        const amountValue = processedValue ? parseFloat(processedValue) : 0;
        const derivedPercentage = getPercentageFromAmount(amountValue, newComponent.type);
        if (derivedPercentage !== null) {
          updated.percentageValue = formatDecimalValue(derivedPercentage, 3);
        }
      }

      // When percentageValue changes, calculate value
      if (field === 'percentageValue') {
        const percentValue = processedValue ? parseFloat(processedValue) : null;
        if (percentValue !== null) {
          let computedAmount = getAmountFromPercentage(percentValue, newComponent.type);
          if (computedAmount !== null) {
            const maxAllowed = getMaxAllowedAmount(newComponent.type);
            if (computedAmount > maxAllowed) {
              computedAmount = maxAllowed;
              const recalculatedPercentage = getPercentageFromAmount(computedAmount, newComponent.type);
              updated.percentageValue = formatDecimalValue(recalculatedPercentage ?? percentValue, 3);
              const limitLabel = newComponent.type === 'reimbursement'
                ? 'reimbursement limit (₹1,00,000)'
                : 'remaining CTC limit';

              // Only show new toast if previous one is closed
              if (!amountErrorToastActiveRef.current) {
                toast.error(`Amount derived from percentage exceeds ${limitLabel}. Adjusted automatically.`);
                amountErrorToastActiveRef.current = true;

                // Clear the flag after toast duration (default is 4000ms from Toaster config)
                setTimeout(() => {
                  amountErrorToastActiveRef.current = false;
                }, 4000);
              }
            }
            updated.value = formatDecimalValue(computedAmount);
          }
        }
      }

      return updated;
    });
  }, [getPercentageFromAmount, getAmountFromPercentage, getMaxAllowedAmount, newComponent.type]);

  // Add component to the list
  const addComponent = useCallback(() => {
    if (!newComponent.name) {
      setComponentNameError(true);
      setComponentNameAlreadyUsed(false);
      return;
    }

    // Check if component name is already used
    const isNameAlreadyUsed = formData.components.some(
      comp => comp.name.toLowerCase() === newComponent.name.toLowerCase()
    );

    if (isNameAlreadyUsed) {
      setComponentNameAlreadyUsed(true);
      setComponentNameError(false);
      return;
    }

    // Clear errors if component name is valid
    setComponentNameError(false);
    setComponentNameAlreadyUsed(false);

    if (!newComponent.value || parseFloat(newComponent.value) <= 0) {
      toast.error('Please enter a valid amount for the component');
      return;
    }

    const ctc = parseFloat(formData.ctc) || 0;
    const componentAmount = parseFloat(newComponent.value) || 0;

    // Validate that earnings + deductions don't exceed CTC (reimbursements are excluded from this check)
    if (ctc > 0 && (newComponent.type === 'earning' || newComponent.type === 'deduction')) {
      const currentNonReimbursementTotal = formData.components
        .filter(comp => comp.type !== 'reimbursement')
        .reduce((sum, comp) => sum + toNumber(comp.value), 0);
      const newTotal = currentNonReimbursementTotal + componentAmount;

      if (newTotal > ctc) {
        toast.error(`Cannot add component: Total of earnings and deductions (₹${newTotal.toLocaleString('en-IN')}) would exceed CTC (₹${ctc.toLocaleString('en-IN')})`);
        return;
      }
    }

    // Validate reimbursement limit
    if (newComponent.type === 'reimbursement' && componentAmount > 100000) {
      toast.error('Reimbursement amount cannot exceed ₹1,00,000.');
      return;
    }

    // Determine category based on component type and name
    const getCategory = (componentName, componentType) => {
      const nameLower = componentName.toLowerCase();
      if (componentType === 'earning') {
        if (nameLower.includes('basic') || nameLower.includes('pay')) return 'basic';
        if (nameLower.includes('allowance') || nameLower.includes('hra') || nameLower.includes('conveyance') || nameLower.includes('medical') || nameLower.includes('field') || nameLower.includes('travel') || nameLower.includes('shift')) return 'allowance';
        if (nameLower.includes('incentive') || nameLower.includes('bonus') || nameLower.includes('overtime')) return 'bonus';
        return 'other';
      } else if (componentType === 'deduction') {
        if (nameLower.includes('pf') || nameLower.includes('provident')) return 'statutory';
        if (nameLower.includes('esic') || nameLower.includes('esi')) return 'statutory';
        if (nameLower.includes('tax') || nameLower.includes('tds') || nameLower.includes('professional')) return 'tax';
        if (nameLower.includes('insurance') || nameLower.includes('mediclaim')) return 'other';
        return 'other';
      } else {
        return 'reimbursement';
      }
    };

    const component = {
      id: Date.now(),
      name: newComponent.name,
      type: newComponent.type,
      category: getCategory(newComponent.name, newComponent.type),
      value: componentAmount,
      percentageValue: parseFloat(newComponent.percentageValue) || 0
    };

    setFormData(prev => ({
      ...prev,
      components: [...prev.components, component]
    }));

    // Reset new component
    setNewComponent(getInitialComponentState());

    // Clear validation errors
    setComponentNameError(false);
    setComponentNameAlreadyUsed(false);

    // Clear components error if it exists
    if (fieldErrors.components) {
      setFieldErrors(prev => ({
        ...prev,
        components: ''
      }));
    }
  }, [newComponent, formData.ctc, formData.components, fieldErrors]);

  // Remove component from the list
  const removeComponent = useCallback((id) => {
    setFormData(prev => ({
      ...prev,
      components: prev.components.filter(comp => comp.id !== id)
    }));
  }, []);

  const createMutation = useMutation({
    mutationFn: createSalaryStructure,
    onSuccess: (data) => {
      toast.success('Salary structure created successfully!');
      // Invalidate and refetch salary structures list
      queryClient.invalidateQueries({ queryKey: ['salaryStructures'] });

      if (onSuccess) {
        onSuccess();
        if (onClose) onClose();
      } else {
        // Navigate back to salary structures list
        navigate('/salary_structure');
      }
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create salary structure';
      toast.error(errorMessage);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateSalaryStructure(id, data),
    onSuccess: () => {
      toast.success('Salary structure updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['salaryStructures'] });

      if (onSuccess) {
        onSuccess();
        if (onClose) onClose();
      } else {
        navigate('/salary_structure');
      }
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update salary structure';
      toast.error(errorMessage);
    }
  });

  // Get calculated totals
  const totals = calculateTotals();
  const isSubmitting = isEditMode ? updateMutation.isPending : createMutation.isPending;
  const submitButtonLabel = isEditMode ? 'Update Structure' : 'Create Structure';
  const submitLoadingLabel = isEditMode ? 'Updating...' : 'Creating...';
  const submitPermissionAction = isEditMode ? 'update' : 'create';

  // Validate form fields
  const validateForm = useCallback(() => {
    const errors = {
      name: '',
      code: '',
      department: '',
      grade: '',
      ctc: '',
      components: ''
    };

    let hasErrors = false;

    if (!formData.name || !formData.name.trim()) {
      errors.name = 'Structure name is required';
      hasErrors = true;
    }

    if (!formData.code || !formData.code.trim()) {
      errors.code = 'Structure code is required';
      hasErrors = true;
    }

    if (!formData.department || !formData.department.trim()) {
      errors.department = 'Department is required';
      hasErrors = true;
    }

    if (!formData.grade || !formData.grade.trim()) {
      errors.grade = 'Level is required';
      hasErrors = true;
    }

    if (!formData.ctc || parseFloat(formData.ctc) <= 0) {
      errors.ctc = 'CTC must be greater than 0';
      hasErrors = true;
    }

    if (formData.components.length === 0) {
      errors.components = 'Please add at least one component';
      hasErrors = true;
    }

    setFieldErrors(errors);
    return !hasErrors;
  }, [formData]);

  // Handle form submission
  const handleSubmit = useCallback(() => {
    // Validate all fields
    if (!validateForm()) {
      return;
    }

    // Prepare data for API (remove temporary id from components)
    // Note: structureId will be auto-generated in the backend
    const submitData = {
      name: formData.name.trim(),
      code: formData.code.trim(),
      department: formData.department.trim(),
      grade: formData.grade.trim(),
      ctc: parseFloat(formData.ctc),
      status: formData.status,
      components: formData.components.map(({ id, ...component }) => component)
    };

    if (isEditMode) {
      if (!editingStructureId) {
        toast.error('Unable to determine which salary structure to update.');
        return;
      }
      updateMutation.mutate({ id: editingStructureId, data: submitData });
      return;
    }

    // Submit to API for creation
    createMutation.mutate(submitData);
  }, [formData, isEditMode, editingStructureId, createMutation, updateMutation]);

  const toastContainer = (
    <Toaster
      position="top-right"
      reverseOrder={false}
      gutter={8}
      toastOptions={{
        duration: 4000,
        style: {
          background: '#363636',
          color: '#fff',
          borderRadius: '8px',
          padding: '12px 16px',
        },
        success: {
          duration: 3000,
          iconTheme: {
            primary: '#10b981',
            secondary: '#fff',
          },
          style: {
            background: '#10b981',
            color: '#fff',
          },
        },
        error: {
          duration: 4000,
          iconTheme: {
            primary: '#ef4444',
            secondary: '#fff',
          },
          style: {
            background: '#ef4444',
            color: '#fff',
          },
        },
      }}
    />
  );

  const headerSection = (
    <div className="mb-6">
      <div className="flex items-center space-x-4">
        <Button
          onClick={handleBack}
          variant="outline"
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
          <p className="text-gray-600 mt-1">{pageSubtitle}</p>
        </div>
      </div>
    </div>
  );

  if (isEditMode && (isLoadingStructure || (!editStructurePayload && !loadStructureError))) {
    return (
      <div className="p-4 sm:p-6">
        {toastContainer}
        {headerSection}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 flex flex-col items-center justify-center min-h-[40vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand mb-4"></div>
          <p className="text-gray-600 text-sm">Loading salary structure details...</p>
        </div>
      </div>
    );
  }

  if (isEditMode && loadStructureError && !isLoadingStructure) {
    return (
      <div className="p-4 sm:p-6">
        {toastContainer}
        {headerSection}
        <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6">
          <div className="flex items-start gap-3">
            <div className="text-red-500 mt-0.5">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Unable to load salary structure</h2>
              <p className="text-sm text-gray-600 mt-1">{loadStructureError}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button
              onClick={retryLoadStructure}
              className="w-full sm:w-auto px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors text-sm font-medium"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/salary_structure')}
              className="w-full sm:w-auto px-4 py-2 border border-brand text-brand rounded-lg hover:bg-brand/5 transition-colors text-sm font-medium"
            >
              Go back to list
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {toastContainer}
      {headerSection}

      <div className="rounded-xl w-full mx-auto overflow-hidden flex flex-col" style={{ backgroundColor: 'rgb(249, 250, 251)' }}>
        {/* Form Content */}
        <div className="flex-1 overflow-y-auto py-4 px-1 space-y-6 sm:space-y-8">
          {/* Basic Information Section */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
              <div className="p-1.5 bg-brand/10 rounded-lg">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-brand" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Basic Information</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 pt-2">
              <div>
                <label className="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  <Briefcase className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 text-gray-400" />
                  Structure Name <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleTextInput('name', e.target.value)}
                  onBlur={() => handleFieldBlur('name')}
                  onKeyPress={handleTextKeyPress}
                  maxLength={100}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:outline-none focus:ring-0 transition-all text-sm ${fieldErrors.name
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-brand focus:border-brand'
                    }`}
                  placeholder="e.g., Software Engineer - L1"
                  required
                />
                {fieldErrors.name ? (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.name}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.name.length}/100 characters
                  </p>
                )}
              </div>

              <div>
                <label className="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  <Hash className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 text-gray-400" />
                  Structure Code <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleTextInput('code', e.target.value)}
                  onBlur={() => handleFieldBlur('code')}
                  onKeyPress={handleTextKeyPress}
                  maxLength={20}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:outline-none focus:ring-0 transition-all text-sm ${fieldErrors.code
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-brand focus:border-brand'
                    }`}
                  placeholder="e.g., SE-L1"
                  required
                />
                {fieldErrors.code ? (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.code}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.code.length}/20 characters
                  </p>
                )}
              </div>

              <div>
                <label className="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 text-gray-400" />
                  Department <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  {loadingDepartments ? (
                    <div className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-gray-50 flex items-center text-sm text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand mr-2"></div>
                      Loading departments...
                    </div>
                  ) : (
                    <select
                      value={formData.department}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, department: e.target.value }));
                        if (fieldErrors.department) {
                          setFieldErrors(prev => ({ ...prev, department: '' }));
                        }
                      }}
                      onBlur={() => handleFieldBlur('department')}
                      className={`w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 border rounded-lg focus:outline-none focus:ring-0 transition-all appearance-none bg-white text-sm ${fieldErrors.department
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-brand focus:border-brand'
                        }`}
                      required
                    >
                      <option value="">Select Department</option>
                      {departmentsData && departmentsData.length > 0 ? (
                        departmentsData.map((dept, index) => {
                          const name = typeof dept === 'string' ? dept : (dept.name || dept.departmentName || dept.title || '');
                          const value = typeof dept === 'string' ? dept : (dept.name || dept.id || dept._id || index);
                          return (
                            <option key={typeof dept === 'object' ? (dept._id || dept.id || index) : `${dept}-${index}`} value={name}>
                              {name}
                            </option>
                          );
                        })
                      ) : (
                        <option value="" disabled>No active departments available</option>
                      )}
                    </select>
                  )}
                </div>
                {fieldErrors.department && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.department}</p>
                )}
              </div>

              <div>
                <label className="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  <Layers className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 text-gray-400" />
                  Level <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData.grade}
                  onChange={(e) => handleTextInput('grade', e.target.value)}
                  onBlur={() => handleFieldBlur('grade')}
                  onKeyPress={handleTextKeyPress}
                  maxLength={20}
                  className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg focus:outline-none focus:ring-0 transition-all text-sm ${fieldErrors.grade
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:ring-brand focus:border-brand'
                    }`}
                  placeholder="e.g., L1, M1, S1"
                  required
                />
                {fieldErrors.grade ? (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.grade}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.grade.length}/20 characters
                  </p>
                )}
              </div>

              <div className="md:col-span-1">
                <label className="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  <IndianRupee className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 text-gray-400" />
                  CTC (Cost to Company) <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.ctc}
                    onChange={(e) => handleCTCChange(e.target.value)}
                    onBlur={() => handleFieldBlur('ctc')}
                    onKeyDown={handleCTCKeyDown}
                    min="0"
                    max="10000000"
                    disabled={formData.components.length > 0}
                    className={`w-full px-4 py-2 sm:py-2.5 border rounded-lg focus:outline-none focus:ring-0 transition-all text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${formData.components.length > 0
                      ? 'bg-gray-50 cursor-not-allowed opacity-60 border-gray-300'
                      : fieldErrors.ctc
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-brand focus:border-brand'
                      }`}
                    placeholder="e.g., 600000"
                    required
                  />
                  {fieldErrors.ctc ? (
                    <p className="text-xs text-red-600 mt-1">{fieldErrors.ctc}</p>
                  ) : (
                    <>
                      {formData.components.length > 0 ? (
                        <p className="text-xs text-gray-500 mt-1">
                          CTC cannot be changed after adding components. Remove all components to edit.
                        </p>
                      ) : (
                        <>
                          <p className="text-xs text-gray-500 mt-1">
                            {parseInt(formData.ctc || 0).toLocaleString('en-IN')}/{parseInt(10000000).toLocaleString('en-IN')} (max)
                          </p>
                          {formData.ctc && parseInt(formData.ctc) >= 10000000 && (
                            <p className="text-xs text-red-600 mt-1">
                              Maximum limit reached (₹{parseInt(10000000).toLocaleString('en-IN')})
                            </p>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="md:col-span-1">
                <label className="flex items-center text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  <Receipt className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 text-gray-400" />
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand transition-all text-sm capitalize bg-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Salary Components Section */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 pb-2 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-brand/10 rounded-lg">
                  <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-brand" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Salary Components</h3>
              </div>
              <span className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 sm:px-3 py-1 rounded-full">
                {formData.components.length} {formData.components.length === 1 ? 'Component' : 'Components'}
              </span>
            </div>

            {/* Components Error Message */}
            {fieldErrors.components && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{fieldErrors.components}</p>
              </div>
            )}

            {/* CTC Required Message */}
            {!formData.ctc || parseFloat(formData.ctc) <= 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <Calculator className="w-5 h-5 text-yellow-600 mt-0.5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800">
                      Please enter CTC (Cost to Company) first
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Existing Components */}
            {formData.components.length > 0 && (
              <div className="space-y-3">
                {formData.components.map((component) => (
                  <div
                    key={component.id}
                    className="bg-gradient-to-r from-gray-50 to-white p-4 sm:p-5 rounded-xl border border-gray-200 hover:border-brand/50 transition-all shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-semibold text-gray-900">{component.name}</p>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${component.type === 'earning' ? 'bg-green-100 text-green-800' :
                            component.type === 'deduction' ? 'bg-red-100 text-red-800' :
                              'bg-brand/10 text-brand'
                            }`}>
                            {component.type}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>Amount: ₹{component.value.toLocaleString('en-IN')}</span>
                          {component.percentageValue > 0 && (
                            <span>Percentage: {Number(component.percentageValue).toFixed(3)}%</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => removeComponent(component.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        aria-label="Remove component"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Component */}
            <div className={`bg-brand/5 p-4 sm:p-5 rounded-xl border-2 border-dashed border-brand/20 ${(!formData.ctc || parseFloat(formData.ctc) <= 0) ? 'opacity-60' : ''}`}>
              <h4 className="text-sm sm:text-md font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <Plus className="w-4 h-4 mr-2 text-brand" />
                Add New Component
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3 sm:gap-4">
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Component Name <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newComponent.name}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      const component = salaryComponentsResult.find(c => c.name === selectedName);

                      setNewComponent(prev => ({
                        ...prev,
                        name: selectedName,
                        ...(component && { type: component.type || 'earning' })
                      }));

                      // Check if the selected name is already used
                      if (selectedName) {
                        const isNameAlreadyUsed = formData.components.some(
                          comp => comp.name.toLowerCase() === selectedName.toLowerCase()
                        );
                        setComponentNameAlreadyUsed(isNameAlreadyUsed);
                        setComponentNameError(false);
                      } else {
                        setComponentNameError(false);
                        setComponentNameAlreadyUsed(false);
                      }
                    }}
                    disabled={!formData.ctc || parseFloat(formData.ctc) <= 0}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0 text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 ${componentNameError || componentNameAlreadyUsed
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-brand/30 focus:ring-brand focus:border-brand'
                      }`}
                    required
                  >
                    <option value="" disabled={loadingComponents}>
                      {loadingComponents ? 'Loading components...' : 'Select Component'}
                    </option>
                    {salaryComponentsResult.map((comp) => (
                      <option key={comp._id || comp.id} value={comp.name}>
                        {comp.name}
                      </option>
                    ))}
                  </select>
                  {componentNameError && (
                    <p className="text-xs text-red-600 mt-1">Please select a component name</p>
                  )}
                  {componentNameAlreadyUsed && (
                    <p className="text-xs text-red-600 mt-1">This component name is already used</p>
                  )}
                </div>

                <div className="md:col-span-1 lg:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Type</label>
                  <select
                    value={newComponent.type}
                    onChange={(e) => setNewComponent(prev => ({ ...prev, type: e.target.value }))}
                    disabled={!formData.ctc || parseFloat(formData.ctc) <= 0}
                    className="w-full px-3 py-2 border border-brand/30 rounded-lg focus:outline-none outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <option value="earning">Earning</option>
                    <option value="deduction">Deduction</option>
                    <option value="reimbursement">Reimbursement</option>
                  </select>
                </div>

                <div className="md:col-span-1 lg:col-span-2">
                  <label className="text-xs font-medium text-gray-700 mb-1.5 flex items-center">
                    <IndianRupee className="w-3 h-3 mr-1" />
                    Amount
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={newComponent.value}
                      onChange={(e) => handleComponentChange('value', e.target.value)}
                      min="0"
                      max={getMaxAllowedAmount(newComponent.type)}
                      disabled={!formData.ctc || parseFloat(formData.ctc) <= 0}
                      className="w-full px-3 py-2 border border-brand/30 rounded-lg focus:outline-none outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available: ₹{getAvailableAmount(newComponent.type).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>

                <div className="md:col-span-1 lg:col-span-2">
                  <label className="text-xs font-medium text-gray-700 mb-1.5 flex items-center">
                    <Percent className="w-3 h-3 mr-1" />
                    Percentage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={newComponent.percentageValue}
                      onChange={(e) => handleComponentChange('percentageValue', e.target.value)}
                      min="0"
                      max="100"
                      disabled={newComponent.type !== 'reimbursement' && (!formData.ctc || parseFloat(formData.ctc) <= 0)}
                      className="w-full px-3 py-2 border border-brand/30 rounded-lg focus:outline-none outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      placeholder="0"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {newComponent.percentageValue || 0}/100%
                    </p>
                  </div>
                </div>

                <div className="md:col-span-2 lg:col-span-3 flex align-center justify-center">
                  <div className="w-full pt-5">
                    <PermissionGate permission="payroll" action="update">
                      <button
                        type="button"
                        onClick={addComponent}
                        disabled={!formData.ctc || parseFloat(formData.ctc) <= 0}
                        className="w-full px-3 sm:px-4 py-1 sm:py-2.5 bg-brand text-white rounded-lg hover:bg-brand-dark transition-all text-sm font-medium shadow-md hover:shadow-lg flex items-center justify-center border-transparent disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:hover:bg-gray-400"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Add</span>
                      </button>
                    </PermissionGate>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Section */}
          <div className="bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-2 mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-gray-200">
              <div className="p-1.5 bg-brand/10 rounded-lg">
                <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-brand" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Salary Summary</h3>
            </div>
            {(() => {
              const ctc = parseFloat(formData.ctc) || 0;
              const totalNonReimbursements = formData.components
                .filter(comp => comp.type !== 'reimbursement')
                .reduce((sum, comp) => sum + toNumber(comp.value), 0);
              const exceedsCTC = totalNonReimbursements > ctc && ctc > 0;
              return exceedsCTC ? (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 font-medium">
                    ⚠️ Warning: Total of earnings and deductions (₹{totalNonReimbursements.toLocaleString('en-IN')}) exceeds CTC (₹{ctc.toLocaleString('en-IN')})
                  </p>
                </div>
              ) : null;
            })()}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-green-50 p-3 sm:p-4 rounded-lg border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total Earnings</label>
                  <TrendingUp className="w-4 h-4 text-green-600 flex-shrink-0" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-green-700">₹{totals.earnings.toLocaleString('en-IN')}</div>
              </div>

              <div className="bg-brand/5 p-3 sm:p-4 rounded-lg border border-brand/20">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Reimbursements</label>
                  <Receipt className="w-4 h-4 text-brand flex-shrink-0" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-brand">₹{totals.reimbursements.toLocaleString('en-IN')}</div>
              </div>

              <div className="bg-red-50 p-3 sm:p-4 rounded-lg border border-red-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total Deductions</label>
                  <TrendingDown className="w-4 h-4 text-red-600 flex-shrink-0" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-red-700">₹{totals.deductions.toLocaleString('en-IN')}</div>
              </div>

              <div className="bg-brand/5 p-3 sm:p-4 rounded-lg border border-brand/20">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Net Pay</label>
                  <IndianRupee className="w-4 h-4 text-brand flex-shrink-0" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-brand">₹{totals.net.toLocaleString('en-IN')}</div>
                <div className="text-xs text-gray-500 mt-1 hidden sm:block">Earnings + Reimbursements</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3">
          <button
            onClick={handleBack}
            className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-red-600 text-white border-transparent rounded-lg hover:bg-red-700 transition-colors font-medium text-sm sm:text-base"
          >
            Cancel
          </button>

          <PermissionGate permission="payroll" action={submitPermissionAction}>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={formData.components.length === 0 || isSubmitting}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-brand text-white rounded-lg hover:bg-brand-dark transition-all font-medium shadow-md hover:shadow-lg flex items-center justify-center space-x-2 text-sm sm:text-base border-transparent disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:hover:from-gray-400 disabled:hover:to-gray-400"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span className="whitespace-nowrap">{submitLoadingLabel}</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span className="whitespace-nowrap">{submitButtonLabel}</span>
                </>
              )}
            </button>
          </PermissionGate>
        </div>
      </div>
    </div>
  );
};

export default CreateSalaryStructure;

